import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useInput } from '../hooks/useInput';
import {
  asyncReceiveThreadDetail,
  asyncAddComment,
} from '../states/threadDetail/action';
import {
  toggleLikeThreadDetail,
  toggleDislikeThreadDetail,
  toggleLikeComment,
  toggleDislikeComment,
  isVoted,
} from '../utils/vote';
import ThreadCard from '../components/ThreadCard';
import FilterButton from '../components/utilities/FilterButton';
import CustomTextarea from '../components/utilities/CustomTextarea';
import Button from '../components/utilities/Button';
import BackIcon from '../components/icons/BackIcon';

export default function DetailPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { threadDetail = {}, authUser } = useSelector((state) => state);
  const [thread, setThread] = useState({});
  const [sort, setSort] = useState('oldest');
  const [content, setContent] = useInput();
  const navigate = useNavigate();

  const handleSubmitComment = async () => {
    const isSuccess = await dispatch(asyncAddComment(id, content));
    if (isSuccess) {
      setContent('');
      toast.success('Comment saved successfully');
    }
  };

  const onBack = () => navigate(-1);

  useMemo(
    (type) => {
      if (sort === 'oldest') {
        thread.comments?.sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      } else if (sort === 'newest') {
        thread.comments?.sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } else if (sort === 'top') {
        thread.comments?.sort(
          (a, b) => b.upVotesBy.length - a.upVotesBy.length
        );
      }
    },
    [sort, thread.comments]
  );

  useEffect(() => {
    dispatch(asyncReceiveThreadDetail(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (threadDetail) {
      const newThreadDetail = JSON.parse(JSON.stringify(threadDetail));
      setThread(newThreadDetail);
    }
  }, [sort, threadDetail]);

  return (
    <section>
      {threadDetail ? (
        <>
          <button
            type="button"
            className="flex items-center justify-center gap-y-2 rounded-lg py-3 px-4 text-sm outline-none whitespace-nowrap hover:text-cyan-600 dark:hover:text-cyan-600 focus-visible:text-cyan-600 dark:focus-visible:text-cyan-600 -ml-4 mb-4"
            onClick={onBack}>
            <BackIcon />
            <p className="ml-2">Back</p>
          </button>
          <h1 className="text-xl md:text-2xl lg:text-4xl font-medium lg:font-semibold">
            {threadDetail?.title}
          </h1>
          <div className="text-zinc-500 dark:text-zinc-400 mt-2 mb-4">
            <span className="font-bold">{threadDetail?.owner?.name}</span>
            <span>{` started thread with `}</span>
            <span className="font-bold">{threadDetail?.category}</span>
            <span>{` category`}</span>
          </div>
          <ThreadCard
            owner={threadDetail?.owner?.name}
            avatar={threadDetail?.owner?.avatar}
            createdAt={threadDetail?.createdAt}
            content={threadDetail?.body}
            likeCount={threadDetail?.upVotesBy?.length}
            dislikeCount={threadDetail?.downVotesBy?.length}
            liked={isVoted(threadDetail?.upVotesBy, authUser?.id)}
            disliked={isVoted(threadDetail?.downVotesBy, authUser?.id)}
            onToggleLike={() =>
              toggleLikeThreadDetail({
                voteData: threadDetail?.upVotesBy,
                threadId: threadDetail?.id,
                userId: authUser?.id,
              })
            }
            onToggleDislike={() =>
              toggleDislikeThreadDetail({
                voteData: threadDetail?.downVotesBy,
                threadId: threadDetail?.id,
                userId: authUser?.id,
              })
            }
          />
          <div className="flex flex-col md:flex-row justify-between gap-4 mb-8">
            <div>{threadDetail?.comments?.length} comments</div>
            {threadDetail?.comments?.length > 1 && (
              <div className="flex">
                <FilterButton
                  label="Oldest"
                  isStart
                  onClick={() => setSort('oldest')}
                  isActive={sort === 'oldest'}
                />
                <FilterButton
                  label="Newest"
                  isActive={sort === 'newest'}
                  onClick={() => setSort('newest')}
                />
                <FilterButton
                  label="Top"
                  isActive={sort === 'top'}
                  isEnd
                  onClick={() => setSort('top')}
                />
              </div>
            )}
          </div>
          {thread?.comments?.map((comment) => (
            <ThreadCard
              key={comment?.id}
              owner={comment?.owner?.name}
              avatar={comment?.owner?.avatar}
              createdAt={comment?.createdAt}
              content={comment?.content}
              likeCount={comment?.upVotesBy?.length}
              dislikeCount={comment?.downVotesBy?.length}
              liked={authUser && comment?.upVotesBy.includes(authUser?.id)}
              disliked={authUser && comment?.downVotesBy.includes(authUser?.id)}
              onToggleLike={() =>
                toggleLikeComment({
                  voteData: comment?.upVotesBy,
                  threadId: threadDetail?.id,
                  commentId: comment?.id,
                  userId: authUser?.id,
                })
              }
              onToggleDislike={() =>
                toggleDislikeComment({
                  voteData: comment?.downVotesBy,
                  threadId: threadDetail?.id,
                  commentId: comment?.id,
                  userId: authUser?.id,
                })
              }
            />
          ))}
          <div className="flex flex-col mt-12">
            <p>Write your response</p>
            {authUser ? (
              <>
                <CustomTextarea
                  id="content"
                  value={content}
                  onChange={setContent}
                />
                <div className="flex mt-4 justify-end w-full">
                  <Button
                    label="Comment"
                    className="w-full md:max-w-max"
                    onClick={handleSubmitComment}
                  />
                </div>
              </>
            ) : (
              <p className="mt-8">
                <Link
                  to="/login"
                  className="text-cyan-500 dark:text-cyan-700 hover:text-cyan-600 dark:hover:text-cyan-600 hover:underline mr-1">
                  Login
                </Link>
                to comment
              </p>
            )}
          </div>
        </>
      ) : (
        'Loading..'
      )}
    </section>
  );
}
